import { useGoogleFont, iconFor } from './shared';

/* "Bold" direction: full-bleed color hero, rounded service cards, confident
   display type. The more contemporary/energetic of the two directions --
   deliberately built with different corner radii and rhythm than Editorial
   so the two options actually feel like different agencies, not reskins. */
const TemplateBold = ({ brandKit, content }) => {
  const colors = brandKit?.colors || {};
  const bg = colors.background?.hex || '#FFFFFF';
  const fg = colors.foreground?.hex || '#1A1A1A';
  const primary = colors.primary?.hex || '#1B3A5C';
  const secondary = colors.secondary?.hex || primary;
  const accent = colors.accent?.hex || primary;
  const headingFont = brandKit?.typography?.heading_font || 'Space Grotesk';
  const bodyFont = brandKit?.typography?.body_font || 'Work Sans';
  const logo = brandKit?.logos?.primary;
  const businessName = brandKit?.business_name || 'Your Business';

  useGoogleFont(headingFont);
  useGoogleFont(bodyFont);

  const services = content?.services?.length ? content.services : [];
  const gallery = content?.gallery?.filter((g) => g.image_url) || [];
  const stats = content?.stats?.filter((s) => s.value) || [];
  const processSteps = content?.process_steps?.filter((p) => p.title) || [];
  const team = content?.team?.filter((t) => t.name) || [];
  const faq = content?.faq?.filter((f) => f.question) || [];
  const heading = { fontFamily: `'${headingFont}', sans-serif` };

  return (
    <div style={{ background: bg, color: fg, fontFamily: `'${bodyFont}', sans-serif` }} className="w-full">
      {/* Nav */}
      <div className="flex items-center justify-between px-8 py-5">
        <div className="flex items-center gap-2">
          {logo ? (
            <img src={logo} alt={businessName} className="h-8 w-8 object-contain rounded-lg" />
          ) : (
            <div className="h-8 w-8 rounded-lg" style={{ background: primary }} />
          )}
          <span style={heading} className="text-lg font-bold">{businessName}</span>
        </div>
        <button className="px-5 py-2.5 rounded-full text-sm font-semibold" style={{ background: primary, color: bg }}>
          {content?.cta_button_text || 'Get Started'}
        </button>
      </div>

      {/* Hero -- full-bleed photo with a color-tint overlay, or a flat color block if no image yet */}
      <div
        className="relative mx-4 mt-2 rounded-3xl px-8 py-20 text-center overflow-hidden"
        style={content?.hero_image_url ? {} : { background: primary, color: bg }}
      >
        {content?.hero_image_url && (
          <>
            <img src={content.hero_image_url} alt={businessName} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${primary}99, ${primary}E6)` }} />
          </>
        )}
        <div className="relative" style={{ color: bg }}>
          <span
            className="inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide mb-6"
            style={{ background: bg, color: primary }}
          >
            {content?.eyebrow || 'Studio'}
          </span>
          <h1 style={heading} className="text-5xl font-bold leading-tight mb-5 max-w-2xl mx-auto">
            {content?.headline || 'A headline that says exactly who you are.'}
          </h1>
          <p className="opacity-90 mb-8 max-w-lg mx-auto">
            {content?.subheadline || 'A supporting line that explains the offer clearly.'}
          </p>
          <button className="px-7 py-3.5 rounded-full text-sm font-semibold" style={{ background: bg, color: primary }}>
            {content?.cta_button_text || 'Get Started'}
          </button>
        </div>
      </div>

      {/* Services -- rounded card grid */}
      <div className="px-8 py-16">
        <h2 style={heading} className="text-2xl font-bold mb-8 text-center">What we do</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl mx-auto">
          {services.map((s, i) => {
            const Icon = iconFor(s.icon_hint);
            return (
              <div key={i} className="rounded-2xl p-6" style={{ background: `${primary}0d`, border: `1px solid ${primary}26` }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: accent }}>
                  <Icon className="w-5 h-5" style={{ color: bg }} />
                </div>
                <h3 style={heading} className="font-bold mb-1.5">{s.title}</h3>
                <p className="opacity-70 text-sm">{s.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Gallery -- optional, rounded image grid */}
      {gallery.length > 0 && (
        <div className="px-8 py-4 mb-8">
          <h2 style={heading} className="text-2xl font-bold mb-8 text-center">Gallery</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {gallery.map((g, i) => (
              <figure key={i} className="rounded-2xl overflow-hidden">
                <div className="aspect-square">
                  <img src={g.image_url} alt={g.caption} className="w-full h-full object-cover" />
                </div>
                {g.caption && <figcaption className="text-xs opacity-60 px-1 py-2">{g.caption}</figcaption>}
              </figure>
            ))}
          </div>
        </div>
      )}

      {/* Stats -- optional, rounded band */}
      {stats.length > 0 && (
        <div className="mx-4 mb-8 rounded-3xl px-8 py-10 grid grid-cols-2 sm:grid-cols-4 gap-6" style={{ background: `${accent}14` }}>
          {stats.map((s, i) => (
            <div key={i} className="text-center">
              <p className="text-3xl font-bold mb-1" style={{ ...heading, color: primary }}>{s.value}</p>
              <p className="text-xs uppercase tracking-wide opacity-60">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Process -- optional, rounded numbered cards */}
      {processSteps.length > 0 && (
        <div className="px-8 py-4 mb-8">
          <h2 style={heading} className="text-2xl font-bold mb-8 text-center">How it works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-3xl mx-auto">
            {processSteps.map((p, i) => (
              <div key={i} className="rounded-2xl p-6 text-center" style={{ background: `${secondary}14` }}>
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center mx-auto mb-3 text-sm font-bold"
                  style={{ background: primary, color: bg }}
                >
                  {i + 1}
                </div>
                <h3 style={heading} className="font-bold mb-1.5">{p.title}</h3>
                <p className="opacity-70 text-sm">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* About + testimonial */}
      <div className="px-8 py-4 mb-16">
        <div className="rounded-3xl p-8 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-8 items-center" style={{ background: `${secondary}14` }}>
          <div>
            <h2 style={heading} className="text-2xl font-bold mb-3">
              {content?.about_title || 'About'}
            </h2>
            <p className="opacity-75 leading-relaxed">{content?.about_body}</p>
          </div>
          <div className="rounded-2xl p-6" style={{ background: bg }}>
            <p className="leading-snug mb-3">&ldquo;{content?.testimonial_quote}&rdquo;</p>
            <p className="text-sm font-semibold opacity-60">{content?.testimonial_author}</p>
          </div>
        </div>
      </div>

      {/* Team -- optional, rounded avatar cards */}
      {team.length > 0 && (
        <div className="px-8 py-4 mb-16">
          <h2 style={heading} className="text-2xl font-bold mb-8 text-center">The Team</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-3xl mx-auto">
            {team.map((t, i) => (
              <div key={i} className="rounded-2xl p-6 text-center" style={{ background: `${primary}0d`, border: `1px solid ${primary}26` }}>
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 text-lg font-bold"
                  style={{ background: primary, color: bg }}
                >
                  {t.name.slice(0, 1).toUpperCase()}
                </div>
                <p className="font-bold">{t.name}</p>
                <p className="text-xs opacity-60 mb-2">{t.role}</p>
                <p className="text-sm opacity-70">{t.bio}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* FAQ -- optional, rounded cards */}
      {faq.length > 0 && (
        <div className="px-8 py-4 mb-16">
          <h2 style={heading} className="text-2xl font-bold mb-8 text-center">Frequently Asked</h2>
          <div className="max-w-2xl mx-auto space-y-3">
            {faq.map((f, i) => (
              <div key={i} className="rounded-2xl p-5" style={{ background: `${secondary}14` }}>
                <p className="font-bold mb-1.5">{f.question}</p>
                <p className="opacity-70 text-sm">{f.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA band */}
      <div className="mx-4 mb-4 rounded-3xl px-8 py-14 text-center" style={{ background: accent, color: bg }}>
        <h2 style={heading} className="text-3xl font-bold mb-3">
          {content?.cta_headline || 'Ready to start?'}
        </h2>
        <p className="opacity-90 mb-6 max-w-md mx-auto">{content?.cta_body}</p>
        <button className="px-7 py-3.5 rounded-full text-sm font-semibold" style={{ background: bg, color: accent }}>
          {content?.cta_button_text || 'Get Started'}
        </button>
      </div>

      {/* Footer */}
      <div className="px-8 py-6 flex items-center justify-between text-xs opacity-50">
        <span>&copy; {new Date().getFullYear()} {businessName}</span>
        <span>{content?.footer_tagline}</span>
      </div>
    </div>
  );
};

export default TemplateBold;
